import { useLDClient } from 'launchdarkly-react-client-sdk';
import { useContext, useEffect, useState } from 'react';
import { FeatureFlagOverrideContext } from '@/common/providers/featureFlagOverrideContext';
import type { FlagKeys } from '@/common/providers/featureFlagProvider';
import { isDevToolsEnabled } from '@/common/utils/dev-tools/isDevToolsEnabled';

/**
 * Custom hook for accessing a single feature flag.
 * More efficient than useFlags() when you only need one flag,
 * as it only re-renders when that specific flag changes.
 *
 * Supports dev tools overrides in development/staging environments.
 *
 * Usage:
 *   const isEnabled = useFlag('myFeatureFlag');
 *
 * @param flagKey - The key of the flag to retrieve
 * @returns The current value of the flag
 */
export function useFlag<K extends keyof FlagKeys>(flagKey: K): FlagKeys[K] {
  const ldClient = useLDClient();
  const context = useContext(FeatureFlagOverrideContext);

  const getDefaultValue = (): FlagKeys[K] => {
    if (isDevToolsEnabled() && context?.overrides?.[flagKey] !== undefined) {
      return context.overrides[flagKey] as FlagKeys[K];
    }

    // Then try LaunchDarkly (all flags are boolean, default to false)
    if (ldClient) {
      return ldClient.variation(flagKey as string, false);
    }
    return false as FlagKeys[K];
  };

  const [flagValue, setFlagValue] = useState<FlagKeys[K]>(getDefaultValue);

  const hasOverride = isDevToolsEnabled() && context?.overrides?.[flagKey] !== undefined;
  const overrideValue = context?.overrides?.[flagKey];

  useEffect(() => {
    if (!ldClient) {
      return;
    }

    const updateFlagValue = () => {
      const currentValue = ldClient.variation(flagKey as string, false);
      setFlagValue(currentValue);
    };

    updateFlagValue();

    // Listen for ready event (flags loaded from server)
    const readyHandler = () => {
      updateFlagValue();
    };

    const changeHandler = (changes: Record<string, { current: unknown; previous: unknown }>) => {
      const change = changes[flagKey];
      if (change) {
        const newValue = change.current as FlagKeys[K];
        setFlagValue(newValue);
      }
    };

    ldClient.on('ready', readyHandler);
    ldClient.on('change', changeHandler);

    return () => {
      ldClient.off('ready', readyHandler);
      ldClient.off('change', changeHandler);
    };
  }, [ldClient, flagKey]);

  return hasOverride ? (overrideValue as FlagKeys[K]) : flagValue;
}
